import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Anchor, NumberFormatter } from '@mantine/core';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'tabler-icons-react';
import { Context as HelperContext } from '../../../../providers/HelperContextProvider';
import AppFlexbox from '../../../common/AppFlexbox';
import AppStack from '../../../common/AppStack';
import AppText from '../../../common/AppText';
import AppTextEditorContent from '../../../common/AppTextEditorContent';
import WsProductMediaShowcase from './sections/productInformation/WsProductMediaShowcase';
import { useMediaQueries } from '../../../../helpers/hooks';
import ResponsiveWebstoreModal from './ResponsiveWebstoreModal';
import WsFinalSaleWarning from './blocks/finalSaleWarning/WsFinalSaleWarning';

const PreviewWebstoreProductModal = ({
  baseStoreUrl,
  mediaQueries,
  editModeEnabled,
  onNavigate,
  pathname
}) => {
  const { isTabletOrSmaller } = mediaQueries || useMediaQueries();
  const { state: helperState, toggleWebstorePreviewProductModal } = useContext(
    HelperContext
  );

  const { ecomStoreProduct } = helperState.webstore.previewWebstoreProductModal;

  const isProductFinalSale =
    ecomStoreProduct &&
    ecomStoreProduct.ecomVendorProduct.ecomVendorProductExternalIds.length > 0;

  const minPrice = ecomStoreProduct
    ? Math.min(...ecomStoreProduct.ecomStoreProductVariants.map((v) => v.price))
    : null;
  const maxPrice = ecomStoreProduct
    ? Math.max(...ecomStoreProduct.ecomStoreProductVariants.map((v) => v.price))
    : null;

  const productUrl = baseStoreUrl
    ? `${baseStoreUrl}/products/${ecomStoreProduct?.urlHandle}`
    : null;

  useEffect(() => {
    if (helperState.webstore.previewWebstoreProductModal.isOpen) {
      // eslint-disable-next-line no-use-before-define
      onClose();
    }
  }, [pathname]);

  const onClose = () => {
    toggleWebstorePreviewProductModal({
      isOpen: false,
      ecomStoreProduct:
        helperState.webstore.previewWebstoreProductModal.ecomStoreProduct
    });
  };

  return (
    <ResponsiveWebstoreModal
      alignModal={isTabletOrSmaller ? 'end' : 'center'}
      editModeEnabled={editModeEnabled}
      isOpen={helperState.webstore.previewWebstoreProductModal.isOpen}
      mediaQueries={mediaQueries}
      noHeader
      onClose={onClose}
      radius={0}
      size={1200}
      zIndex={202}
    >
      {ecomStoreProduct && (
        <AppFlexbox
          style={{
            position: 'relative',
            flexDirection: isTabletOrSmaller ? 'column' : 'row'
          }}
        >
          <AppStack
            style={{
              width: '100%',
              minHeight: isTabletOrSmaller ? 300 : 500,
              maxWidth: isTabletOrSmaller ? 'unset' : '45%',
              gap: 5
            }}
          >
            <WsProductMediaShowcase
              colorScheme={{
                textColor: '#000000',
                backgroundColor: '#ffffff'
              }}
              desktopMediaLayout="carouselThumbnails"
              ecomVendorProductMedia={
                ecomStoreProduct.ecomVendorProduct.ecomVendorProductMedia
              }
              editModeEnabled={editModeEnabled}
              hideBadge={
                ecomStoreProduct.ecomVendorProduct.ecomVendorProductExternalIds
                  ?.length > 0
              }
              mediaQueries={mediaQueries}
              mediaSize="small"
              themeConfig={{}}
              thumbnailColumns={4}
            />
          </AppStack>
          <AppStack
            style={{
              width: '100%',
              gap: 24,
              marginTop: isTabletOrSmaller ? 0 : 20,
              marginLeft: isTabletOrSmaller ? 0 : 40
            }}
          >
            <AppStack style={{ gap: 10 }}>
              <AppStack style={{ gap: 0 }}>
                {productUrl ? (
                  <Anchor
                    component={Link}
                    onClick={onNavigate}
                    style={{ color: '#000', fontSize: 28, fontWeight: 500 }}
                    to={productUrl}
                  >
                    {ecomStoreProduct.ecomVendorProduct.name}
                  </Anchor>
                ) : (
                  <AppText
                    style={{ color: '#000', fontSize: 28, fontWeight: 500 }}
                  >
                    {ecomStoreProduct.ecomVendorProduct.name}
                  </AppText>
                )}
              </AppStack>

              <AppFlexbox style={{ alignItems: 'end', gap: 10 }}>
                <AppFlexbox style={{ alignItems: 'center', gap: 10 }}>
                  <AppText style={{ fontSize: 22, fontWeight: 450 }}>
                    {minPrice !== maxPrice ? 'From ' : ''}
                    <NumberFormatter
                      decimalScale={2}
                      fixedDecimalScale
                      prefix="$"
                      suffix=" CAD"
                      thousandSeparator
                      value={minPrice / 100}
                    />
                  </AppText>
                </AppFlexbox>
              </AppFlexbox>

              <AppTextEditorContent
                content={ecomStoreProduct.ecomVendorProduct.description}
                previewHeight={150}
              />
            </AppStack>

            {isProductFinalSale && <WsFinalSaleWarning />}

            {productUrl && (
              <AppFlexbox>
                <AppFlexbox
                  style={{
                    gap: 8,
                    alignItems: 'center'
                  }}
                >
                  <Anchor
                    component={Link}
                    onClick={onNavigate}
                    style={{ fontSize: 14, color: '#000' }}
                    to={productUrl}
                  >
                    View full details
                  </Anchor>
                  <ArrowRight color="#000" size={16} />
                </AppFlexbox>
              </AppFlexbox>
            )}
          </AppStack>
        </AppFlexbox>
      )}
    </ResponsiveWebstoreModal>
  );
};

PreviewWebstoreProductModal.propTypes = {
  baseStoreUrl: PropTypes.string,
  editModeEnabled: PropTypes.bool,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  pathname: PropTypes.string
};

export default PreviewWebstoreProductModal;
